<template>
  <div class="px-2 py-1">
    <b-row>
      <b-col
        sm="6"
        class="d-flex justify-content-evenly"
      >
        <b-button
          v-if="isTabCampaign && accountAccessManager.canManageCampaigns()"
          :to="'/' +$route.params.accountId + '/campaign/create'"
          size="sm"
          variant="primary"
        >
          <GjIcon name="Plus" />
          <span class="d-none d-lg-inline-block ml-50">
            {{ $tk('ClientPortal.MasterTable.Campaign') }}
          </span>
        </b-button>
        <b-button
          v-if="isTabAdGroup && accountAccessManager.canManageAdGroups()"
          :to="createAdGroupUrl"
          size="sm"
          variant="primary"
        >
          <GjIcon name="Plus" />
          <span class="d-none d-lg-inline-block ml-50">
            {{ $tk('ClientPortal.MasterTable.AdGroup') }}
          </span>

        </b-button>
        <b-button
          v-if="isTabAd && accountAccessManager.canManageAds()"
          :to="createAdCreateUrl"
          size="sm"
          variant="primary"
        >
          <GjIcon name="Plus" />
          <span class="d-none d-lg-inline-block ml-50">
            {{ $tk('ClientPortal.MasterTable.Ad') }}
          </span>
        </b-button>
        <template v-if="accountAccessManager.canManageCampaigns()">
          <b-button
            variant="outline-secondary"
            class="outline-light-secondary ml-1"
            :disabled="dissableBulkOperations"
            size="sm"
            @click="archive"
          >
            <GjIcon name="Box" />
            <span class="d-none d-lg-inline-block ml-50">{{ $tk('ClientPortal.MasterTable.Archive') }}</span>
          </b-button>
          <b-button
            variant="outline-secondary"
            class="outline-light-secondary ml-1"
            :disabled="dissableBulkOperations"
            size="sm"
            @click="remove"
          >
            <GjIcon name="Delete" />
            <span class="d-none d-lg-inline-block ml-50">{{ $tk('ClientPortal.MasterTable.Delete') }}</span>
          </b-button>
          <b-dropdown
            variant="outline-dark"
            size="sm"
            class="ml-1"
            :disabled="dissableBulkOperations"
          >
            <template v-slot:button-content>
              <span class="d-none d-lg-inline-block">
                {{ $tk('ClientPortal.MasterTable.Actions') }}
              </span>
            </template>
            <template v-for="status in wrapper.statuses">
              <b-dropdown-item
                v-if="status.editable"
                :key="status.id"
                @click="changeStatus(status.id)"
              >
                {{ status.title }}
              </b-dropdown-item>
            </template>
          </b-dropdown>
        </template>
        <loader
          v-if="loadingStatistics"
          class="d-inline-block ml-50"
          :size="15"
          :show="true"
        />
      </b-col>
      <b-col
        class="text-right"
        sm="6"
      >
        <b-dropdown
          variant="outline-dark"
          size="sm"
        >
          <template v-slot:button-content>
            <GjIcon name="List_fill" />
            <span class="d-none d-lg-inline-block ml-50">
              {{ breakdownText }}
            </span>
          </template>
          <b-dropdown-item
            v-for="(breakdown, index) in wrapper.breakdowns"
            :key="index"
            link-class="d-flex"
            :class="isBreakdownSet(breakdown) ? 'selected-filter' : ''"
            @click="setBreakdown(breakdown)"
          >
            {{ breakdown.name }}
            <GjIcon
              v-if="isBreakdownSet(breakdown)"
              name="Check"
            />
          </b-dropdown-item>
        </b-dropdown>
        <b-dropdown
          class="ml-50"
          variant="outline-dark"
          size="sm"
        >
          <template v-slot:button-content>
            <GjIcon name="Redirect" />
            <span class="d-none d-lg-inline-block ml-50">
              {{ $tk('ClientPortal.MasterTable.ExportData') }}
            </span>
          </template>
          <b-dropdown-item @click="exportData(0, 1)">
            {{ $tk('ClientPortal.MasterTable.ExportDataAsCSV') }}
          </b-dropdown-item>
          <b-dropdown-item @click="exportData(0, 2)">
            {{ $tk('ClientPortal.MasterTable.ExportDataAsExcel') }}
          </b-dropdown-item>
        </b-dropdown>
        <!--Preset Modal-->
        <b-button
          variant="outline-dark"
          size="sm"
          class="ml-50"
          @click="openPreset"
        >
          <GjIcon name="Filters" />
          <span class="d-none d-lg-inline-block ml-50">
            {{ $tk('ClientPortal.MasterTable.Preset') }}
          </span>
        </b-button>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import StatisticsService from '@/api/client/statistics.service'
import { AccountAccessManager } from '@/utils/accessManager/accessManager'
import loader from '@/components/Loader.vue'

const EXPORT = {
  CSV: 1,
  EXCEL: 2,
}

const TABS = {
  CAMPAIGNS: 'campaigns',
  AD_GROUPS: 'adgroups',
  ADS: 'ads',
}

export default {
  components: {
    GjIcon,
    loader,
  },
  props: {
    // eslint-disable-next-line vue/require-default-prop
    wrapper: Object,
    isTabOverview: {
      type: Boolean,
      default: false,
    },
    isTabCampaign: {
      type: Boolean,
      default: false,
    },
    isTabAdGroup: {
      type: Boolean,
      default: false,
    },
    isTabAd: {
      type: Boolean,
      default: false,
    },
    breakdownText: {
      type: String,
      default: 'Breakdown',
    },
    loadingStatistics: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      savePresetModal: {
        top: 0,
        left: 0,
        show: false,
        value: '',
      },
      drag: false,
      accountAccessManager: AccountAccessManager,
    }
  },
  computed: {
    showSaveSelectedPresetButton() {
      return this.hasPresetChanges && !this.wrapper.config.preset.isDefault
    },
    showSaveAsNewPresetButton() {
      return this.hasPresetChanges
    },
    dragOptions() {
      return {
        animation: 200,
        disabled: false,
        ghostClass: 'ghost',
      }
    },
    dissableBulkOperations() {
      if (this.isTabCampaign) { return this.wrapper.config.filter.campaignIds.length === 0 }
      if (this.isTabAdGroup) { return this.wrapper.config.filter.adGroupIds.length === 0 }
      if (this.isTabAd) { return this.wrapper.config.filter.adIds.length === 0 }
      return false
    },
    createAdGroupUrl() {
      if (this.wrapper.config.filter.campaignIds.length === 1) {
        return `/${this.$route.params.accountId}/adgroup/create?campaignId=${this.wrapper.config.filter.campaignIds[0]}`
      }
      return `/${this.$route.params.accountId}/adgroup/create`
    },
    createAdCreateUrl() {
      if (this.wrapper.config.filter.adGroupIds.length === 1) {
        return `/${this.$route.params.accountId}/ad/create?adGroupId=${this.wrapper.config.filter.adGroupIds[0]}`
      }
      return `/${this.$route.params.accountId}/ad/create`
    },
  },
  methods: {
    archive() {
      this.$emit('archive')
    },
    remove() {
      this.$emit('delete')
    },
    changeStatus(statusId) {
      this.$emit('changeStatus', statusId)
    },
    exportData(type, exportType) {
      if (exportType === EXPORT.CSV) {
        this.exportCsvData(type)
      } else {
        this.exportExcelData(type)
      }
    },
    async exportCsvData(type) {
      // Getting cfg from search parameters since if we share the cfg with someone else, they'll have another id generated on config in case they make some changes
      const documentLocation = document.location
      const url = new URL(documentLocation)
      const cfg = url.searchParams.get('cfg')
      const response = await StatisticsService.exportCsv(type, cfg != null ? cfg : this.wrapper.config.id, this.$route.params.accountId)
      const fileName = this.getFileName('csv')
      this.downloadFile(response, fileName)
    },
    async exportExcelData(type) {
      const documentLocation = document.location
      const url = new URL(documentLocation)
      const cfg = url.searchParams.get('cfg')
      const response = await StatisticsService.exportExcel(type, cfg != null ? cfg : this.wrapper.config.id, this.$route.params.accountId)
      const fileName = this.getFileName('xlsx')
      this.downloadFile(response, fileName)
    },
    getFileName(type) {
      const currentDateTime = new Date()
      const dateTime = `${currentDateTime.getFullYear()}${currentDateTime.getMonth() + 1}${currentDateTime.getDate()}${currentDateTime.getHours()}${currentDateTime.getMinutes()}`
      switch (this.wrapper.tab) {
        case TABS.CAMPAIGNS: return `Campaigns Report - ${dateTime}.${type}`
        case TABS.AD_GROUPS: return `AdGroups Report - ${dateTime}.${type}`
        case TABS.ADS: return `Ads Report - ${dateTime}.${type}`
        default: return `Overview Report - ${dateTime}.${type}`
      }
    },
    downloadFile(file, name) {
      const fileURL = window.URL.createObjectURL(new Blob([file]))
      const fileLink = document.createElement('a')

      fileLink.href = fileURL
      fileLink.setAttribute('download', name)
      document.body.appendChild(fileLink)
      fileLink.click()
    },
    openPreset() {
      this.$emit('openPreset')
    },
    getPresetButtomClass(preset) {
      if (this.wrapper.config.preset.id === preset.id) return 'bg-primary-light active-preset'
      return ''
    },
    setBreakdown(breakdown) {
      this.$emit('breakdown', breakdown)
    },
    isBreakdownSet(breakdown) {
      return this.wrapper.config.filter.breakdown === breakdown.value
    },
  },
}
</script>
